import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, saveSession, clearSession, getSession } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const AUTH_URL = "https://auth.emergentagent.com/";

function extractSessionId(url) {
  if (!url) return null;
  const hash = url.split("#")[1] || "";
  const query = url.split("?")[1]?.split("#")[0] || "";
  for (const part of [hash, query]) {
    const sid = new URLSearchParams(part).get("session_id");
    if (sid) return sid;
  }
  return null;
}

export function startGoogleLogin() {
  const redirect = window.location.origin + "/";
  window.location.href = `${AUTH_URL}?redirect=${encodeURIComponent(redirect)}`;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getSession().user);
  const [loading, setLoading] = useState(true);

  const applyUser = useCallback((u) => {
    setUser(u);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1) Google OAuth redirect back with session_id
      const sid = extractSessionId(window.location.href);
      if (sid) {
        try {
          const res = await api.authSession(sid);
          saveSession(res.session_token, res.user);
          if (!cancelled) applyUser(res.user);
        } catch { /* ignore */ }
        window.history.replaceState({}, document.title, window.location.origin + "/");
        if (!cancelled) setLoading(false);
        return;
      }
      // 2) Existing token → validate
      const { token } = getSession();
      if (token) {
        try {
          const me = await api.me();
          const full = { ...getSession().user, ...me };
          saveSession(token, full);
          if (!cancelled) applyUser(full);
        } catch {
          clearSession();
          if (!cancelled) applyUser(null);
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [applyUser]);

  const login = async (identifier, password) => {
    const res = await api.login(identifier, password);
    saveSession(res.access_token, res.user);
    setUser(res.user);
    return res.user;
  };

  const register = async (payload) => {
    const res = await api.register(payload);
    saveSession(res.access_token, res.user);
    setUser(res.user);
    return res.user;
  };

  const logout = () => {
    clearSession();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, googleLogin: startGoogleLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const homeForRole = (role) => {
  if (role === "owner") return "/owner/dashboard";
  if (role === "admin") return "/admin/ledger";
  if (role === "coach") return "/coach/classes";
  return "/member/home";
};
